import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Pin, PinOff, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ACTIVITY_PANEL_EVENTS,
  ACTIVITY_PANEL_PROPERTIES,
} from '@/constants/analytics/activity-panel-events';
import { usePromptPushNote } from '@/contexts/PushNoteToCmsContext';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import {
  useGetSourceActionsQuery,
  useResolveExternalNoteQuery,
} from '@/services/api/externalNoteService';
import {
  useCreateStaffNoteMutation,
  useDeleteStaffNoteMutation,
  useGetStaffNotesQuery,
  useTogglePinStaffNoteMutation,
} from '@/services/api/staffNotesService';
import type { ExternalNoteActionSchema } from '@/services/types/external-note-types';
import { StaffNote } from '@/services/types/staff-notes-types';

import { CommentBox } from '../domain-events-v2/CommentBox';
import { EventFeedSkeleton } from '../domain-events-v2/EventFeedSkeleton';
import { CmsPushBadge } from '../push-to-cms/CmsPushBadge';

interface NotesPanelProps {
  matterId: string;
}

// Individual note item component
const NoteItem: React.FC<{
  note: StaffNote;
  isHighlighted?: boolean;
  onDelete: (noteId: string) => void;
  onTogglePin: (noteId: string, isPinned: boolean) => void;
  isDeleting?: boolean;
  pushAction?: ExternalNoteActionSchema;
}> = ({ note, isHighlighted, onDelete, onTogglePin, isDeleting, pushAction }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const userName = note.user
    ? `${note.user.first_name} ${note.user.last_name}`.trim() ||
      note.user.username
    : 'Unknown';

  const timeAgo = formatDistanceToNow(new Date(note.created_at), {
    addSuffix: true,
  });

  // Process content to handle @mentions
  const renderContent = (content: string) => {
    // Split by @mentions pattern
    const mentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match = mentionPattern.exec(content);

    while (match !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }
      // Add styled mention
      parts.push(
        <span
          key={match.index}
          className="inline-flex items-center rounded-md bg-blue-50 px-1 py-0.5 text-blue-600 font-medium"
        >
          @{match[1]}
        </span>,
      );
      lastIndex = match.index + match[0].length;
      match = mentionPattern.exec(content);
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTogglePin(note.id, !note.is_pinned);
  };

  const confirmDelete = () => {
    onDelete(note.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <div
        id={`note-${note.id}`}
        className={cn(
          'flex gap-x-2 py-2 px-2 -mx-2 rounded-md transition-colors duration-300 group',
          isHighlighted && 'bg-yellow-100 ring-1 ring-yellow-300',
          note.is_pinned && !isHighlighted && 'bg-amber-50/50',
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Avatar */}
        <div
          className={cn(
            'h-6 w-6 rounded-full flex items-center justify-center flex-none',
            note.is_pinned ? 'bg-amber-100' : 'bg-blue-100',
          )}
        >
          {note.is_pinned ? (
            <Pin className="h-3 w-3 text-amber-600" />
          ) : (
            <MessageSquare className="h-3 w-3 text-blue-600" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-x-1.5">
            <span className="text-xs font-medium text-zinc-900">
              {userName}
            </span>
            <span className="text-[10px] text-zinc-400">{timeAgo}</span>
            {pushAction && <CmsPushBadge action={pushAction} />}
            {/* Action buttons - shown on hover, inline with name */}
            {isHovered && (
              <div className="flex items-center gap-x-0.5 ml-auto">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handlePinClick}
                      className="flex-none p-0.5 rounded hover:bg-zinc-100 transition-colors"
                    >
                      {note.is_pinned ? (
                        <PinOff className="h-3 w-3 text-amber-500 hover:text-amber-600" />
                      ) : (
                        <Pin className="h-3 w-3 text-zinc-400 hover:text-amber-500" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={4}>
                    <p className="text-[10px]">
                      {note.is_pinned ? 'Unpin' : 'Pin to top'}
                    </p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleDeleteClick}
                      disabled={isDeleting}
                      className="flex-none p-0.5 rounded hover:bg-zinc-100 transition-colors"
                    >
                      <Trash2 className="h-3 w-3 text-zinc-400 hover:text-red-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={4}>
                    <p className="text-[10px]">Delete</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
          <div className="mt-1 text-xs text-zinc-700 whitespace-pre-wrap break-words">
            {renderContent(note.content)}
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Notes list
const NotesList: React.FC<{
  notes: StaffNote[];
  highlightedNoteId?: string | null;
  onDeleteNote: (noteId: string) => void;
  onTogglePin: (noteId: string, isPinned: boolean) => void;
  deletingNoteId?: string | null;
  pushActions?: Record<string, ExternalNoteActionSchema>;
}> = ({ notes, highlightedNoteId, onDeleteNote, onTogglePin, deletingNoteId, pushActions }) => {
  if (notes.length === 0) return null;

  return (
    <div className="divide-y divide-zinc-100">
      {notes.map((note) => (
        <NoteItem
          key={note.id}
          note={note}
          isHighlighted={note.id === highlightedNoteId}
          onDelete={onDeleteNote}
          onTogglePin={onTogglePin}
          isDeleting={note.id === deletingNoteId}
          pushAction={pushActions?.[note.id]}
        />
      ))}
    </div>
  );
};

export const NotesPanel: React.FC<NotesPanelProps> = ({ matterId }) => {
  const capture = usePostHogCapture();
  const hasTrackedOpen = useRef(false);
  const [searchParams] = useSearchParams();
  const highlightedNoteId = searchParams.get('note_id');
  const hasScrolledToNote = useRef(false);
  const [pushStatusPollingInterval, setPushStatusPollingInterval] = useState(0);

  const {
    data: notes,
    isLoading,
    error,
    refetch,
  } = useGetStaffNotesQuery(
    { matterId },
    {
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );

  // Pre-fetch CMS resolution to decide if push modal should appear after note creation
  const { data: cmsResolution } = useResolveExternalNoteQuery({ matterId });

  // Batch-fetch CMS push statuses for all visible notes
  const noteIds = useMemo(
    () => (notes ?? []).map((n) => n.id),
    [notes],
  );
  const { data: pushActions } = useGetSourceActionsQuery(
    { matterId, sourceType: 'STAFF_NOTE', sourceIds: noteIds },
    {
      skip: noteIds.length === 0 || !cmsResolution?.cms_type,
      pollingInterval: pushStatusPollingInterval,
      skipPollingIfUnfocused: true,
    },
  );

  const { promptPushNote } = usePromptPushNote();
  const [createStaffNote] = useCreateStaffNoteMutation();
  const [deleteStaffNote] = useDeleteStaffNoteMutation();
  const [togglePinStaffNote] = useTogglePinStaffNoteMutation();
  const [isAddingNote, setIsAddingNote] = React.useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  useEffect(() => {
    const hasPendingPushActions = Object.values(pushActions ?? {}).some(
      ({ status }) => status === 'PENDING' || status === 'IN_PROGRESS',
    );
    setPushStatusPollingInterval(hasPendingPushActions ? 5000 : 0);
  }, [pushActions]);

  // Track panel opened event once on mount (after data loads)
  useEffect(() => {
    if (!isLoading && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      capture(ACTIVITY_PANEL_EVENTS.NOTES_PANEL_OPENED, {
        [ACTIVITY_PANEL_PROPERTIES.PAGE]: 'matters',
        [ACTIVITY_PANEL_PROPERTIES.COMPONENT]: 'notes_panel',
        [ACTIVITY_PANEL_PROPERTIES.MATTER_ID]: matterId,
        [ACTIVITY_PANEL_PROPERTIES.NOTES_COUNT]: notes?.length || 0,
      });
    }
  }, [isLoading, matterId, notes?.length, capture]);

  // Scroll to highlighted note when data loads
  useEffect(() => {
    if (!isLoading && highlightedNoteId && !hasScrolledToNote.current) {
      hasScrolledToNote.current = true;
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const element = document.getElementById(`note-${highlightedNoteId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [isLoading, highlightedNoteId]);

  const handleCommentSubmit = useCallback(
    async (content: string) => {
      setIsAddingNote(true);
      try {
        const createdNote = await createStaffNote({
          matterId,
          note: { content },
        }).unwrap();

        // Track note creation analytics
        const mentionMatches = content.match(/@[\w.@-]+/g) || [];
        capture(ACTIVITY_PANEL_EVENTS.NOTE_CREATED, {
          [ACTIVITY_PANEL_PROPERTIES.PAGE]: 'matters',
          [ACTIVITY_PANEL_PROPERTIES.COMPONENT]: 'notes_panel',
          [ACTIVITY_PANEL_PROPERTIES.MATTER_ID]: matterId,
          [ACTIVITY_PANEL_PROPERTIES.CHARACTER_COUNT]: content.length,
          [ACTIVITY_PANEL_PROPERTIES.HAS_MENTIONS]: mentionMatches.length > 0,
          [ACTIVITY_PANEL_PROPERTIES.MENTION_COUNT]: mentionMatches.length,
        });

        if (cmsResolution?.cms_type && createdNote?.id) {
          promptPushNote({
            matterId,
            rawContent: content,
            triggerType: 'staff_note',
            cmsResolution,
            sourceType: 'STAFF_NOTE',
            sourceId: createdNote.id,
          });
        } else {
          toast.success('Note added successfully');
        }
        await refetch();
      } catch (err) {
        toast.error('Failed to add note. Please try again.');
        // Re-throw so CommentEditor keeps the user's content on failure
        throw err;
      } finally {
        setIsAddingNote(false);
      }
    },
    [createStaffNote, matterId, refetch, capture, cmsResolution, promptPushNote],
  );

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      setDeletingNoteId(noteId);
      try {
        await deleteStaffNote({
          matterId,
          noteId,
        }).unwrap();

        capture(ACTIVITY_PANEL_EVENTS.NOTE_DELETED, {
          [ACTIVITY_PANEL_PROPERTIES.PAGE]: 'matters',
          [ACTIVITY_PANEL_PROPERTIES.COMPONENT]: 'notes_panel',
          [ACTIVITY_PANEL_PROPERTIES.MATTER_ID]: matterId,
        });

        toast.success('Note deleted');
      } catch {
        toast.error('Failed to delete note. Please try again.');
      } finally {
        setDeletingNoteId(null);
      }
    },
    [deleteStaffNote, matterId, capture],
  );

  const handleTogglePin = useCallback(
    async (noteId: string, isPinned: boolean) => {
      try {
        await togglePinStaffNote({
          matterId,
          noteId,
          is_pinned: isPinned,
        }).unwrap();
        toast.success(isPinned ? 'Note pinned' : 'Note unpinned');
      } catch {
        toast.error('Failed to update note');
      }
    },
    [togglePinStaffNote, matterId],
  );

  // Loading state
  if (isLoading) {
    return <EventFeedSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-center">
          <p className="text-xs text-zinc-600">Failed to load notes</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">
            Please try again later
          </p>
        </div>
      </div>
    );
  }

  // Sort notes by created_at descending (newest first)
  const sortedNotes = [...(notes || [])].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  const isEmpty = sortedNotes.length === 0 && !isAddingNote;

  return (
    <div className="flex flex-col h-full">
      {/* Single CommentBox instance — always mounted so drafts survive list changes */}
      <div className="px-3 py-2 border-b border-zinc-100">
        <CommentBox
          onSubmit={handleCommentSubmit}
          draftKey={`notes-draft:${matterId}`}
        />
      </div>

      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-xs text-zinc-500">No notes yet</p>
            <p className="text-[10px] text-zinc-400 mt-0.5">
              Add a note to start the conversation
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {/* Loading Indicator - Skeleton Item */}
          {isAddingNote && (
            <div className="flex gap-x-2 mb-3 animate-pulse">
              <div className="h-6 w-6 rounded-full bg-zinc-200 flex-none" />
              <div className="flex-1 space-y-1.5 py-0.5">
                <div className="h-3 bg-zinc-200 rounded w-1/4" />
                <div className="h-10 bg-zinc-100 rounded-md w-full" />
              </div>
            </div>
          )}
          <NotesList
            notes={sortedNotes}
            highlightedNoteId={highlightedNoteId}
            onDeleteNote={handleDeleteNote}
            onTogglePin={handleTogglePin}
            deletingNoteId={deletingNoteId}
            pushActions={pushActions}
          />
        </div>
      )}
    </div>
  );
};

export default NotesPanel;
